import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useMemo, useState } from 'react'
import toast from 'react-hot-toast'
import { Button } from '@/components/ui/button'
import {
	Dialog,
	DialogBody,
	DialogContent,
	DialogFooter,
	DialogTitle
} from '@/components/ui/dialog'
import {
	Drawer,
	DrawerContent,
	DrawerFooter,
	DrawerHeader,
	DrawerTitle
} from '@/components/ui/drawer'
import {
	AlertTriangleFillIcon,
	CheckCircleFillIcon
} from '@/components/ui/icons'
import { Spinner } from '@/components/ui/spinner'
import { SearchInput } from '@/features/marquee/components/search-input'
import { useMinWidth } from '@/features/marquee/hooks/use-min-width'
import { makeQueryKeys } from '@/features/marquee/query-keys'
import { hostSearchQueryOptions } from '@/features/marquee/query-options'
import {
	attendeeStatusToLabelKey,
	invalidStateMessage
} from '@/features/marquee/utils'
import { debounce, errorHandler } from '@/helpers/utils'
import {
	assignHost,
	type MarqueeAttendee,
	type MarqueeEventState,
	type MarqueeHostSearchResult
} from '@/sdk'
import { type TKey, useTranslation } from '@/translations/i18n'

/** Row targeted by the dialog, from the export preview. */
export interface AssignHostTarget {
	attendeeId: string
	name: string
}

interface AssignHostDialogProps {
	eventId?: string
	/**
	 * The event's lifecycle state, used only to word a wrong-state refusal: the
	 * write answers the shared 409 `invalid-state`, which names no action.
	 */
	eventState?: MarqueeEventState
	/** The row to resolve; the dialog is open while this is set. */
	target: AssignHostTarget | null
	onClose: () => void
	/**
	 * Opens the mark-unresolvable confirmation over this dialog. Omitted at the
	 * check-in desk, where giving up on a row is not offered: that verdict
	 * belongs to export prep, and nothing here walks it back mid-event.
	 */
	onMarkUnresolvable?: (target: AssignHostTarget) => void
}

/** Debounce for the live search, long enough that typing a name is one request. */
const SEARCH_DEBOUNCE_MS = 300

/**
 * What the operator sees after the write, in three shapes: the row exports, the
 * row is pinned but nobody has looked the account up yet, or the row still will
 * not export. The middle one is not a failure and must not be dressed as one:
 * during a live event the backend deliberately skips the account lookup, so
 * "no account was found" would state something nobody did.
 */
type OutcomeTone = 'ready' | 'deferred' | 'blocked'

const outcomeTone = (attendee: MarqueeAttendee): OutcomeTone => {
	if (attendee.status === 'ready') {
		return 'ready'
	}
	return attendee.status === 'unresolved' ? 'deferred' : 'blocked'
}

const outcomeTitleKey = {
	ready: 'marquee:assign_host_success_title',
	deferred: 'marquee:assign_host_deferred_title',
	blocked: 'marquee:assign_host_partial_title'
} as const satisfies Record<OutcomeTone, TKey>

/**
 * The blocked branch covers two different facts, so it is the one that reads the
 * status rather than the tone: an account at parent level is not the same news
 * as no account at all, and the operator's next move differs.
 */
const outcomeBodyKey = (attendee: MarqueeAttendee): TKey => {
	const tone = outcomeTone(attendee)
	if (tone !== 'blocked') {
		return tone === 'ready'
			? 'marquee:assign_host_success_body'
			: 'marquee:assign_host_deferred_body'
	}
	return attendee.status === 'tagged_at_parent'
		? 'marquee:assign_host_parent_body'
		: 'marquee:assign_host_partial_body'
}

/** The searched-and-chosen host, before it is written to the row. */
function HostOption({
	host,
	isSelected,
	onSelect
}: {
	host: MarqueeHostSearchResult
	isSelected: boolean
	onSelect: () => void
}) {
	const { t } = useTranslation()
	return (
		<button
			className={`flex w-full items-center gap-3 rounded-2xl border px-4 py-3 text-left transition-colors ${
				isSelected
					? 'border-neutral-900 bg-neutral-white'
					: 'border-grey-200 bg-neutral-white hover:bg-grey-50'
			}`}
			onClick={onSelect}
			type='button'
		>
			<span
				className={`flex size-5 shrink-0 items-center justify-center rounded-full border-2 ${
					isSelected ? 'border-neutral-900' : 'border-grey-400'
				}`}
			>
				{isSelected && (
					<span className='size-2.5 rounded-full bg-neutral-900' />
				)}
			</span>
			<span className='flex min-w-0 flex-col'>
				<span className='truncate font-medium text-neutral-900 text-sm'>
					{host.name}
				</span>
				<span className='truncate text-neutral-500 text-sm'>
					{t('marquee:assign_host_option_id', {
						id: host.airbnb_host_id
					})}
				</span>
			</span>
		</button>
	)
}

/** The search half: the field, its guidance, and whatever the term returned. */
function SearchStep({
	target,
	query,
	onQueryChange,
	selectedHostId,
	onSelect
}: {
	target: AssignHostTarget
	query: string
	onQueryChange: (value: string) => void
	selectedHostId: string | null
	onSelect: (host: MarqueeHostSearchResult) => void
}) {
	const { t } = useTranslation()
	// Only the settled term hits the network; the field itself stays instant.
	const [debouncedQuery, setDebouncedQuery] = useState(query)
	const pushDebounced = useMemo(
		() => debounce(setDebouncedQuery, SEARCH_DEBOUNCE_MS),
		[]
	)

	const handleChange = (value: string) => {
		onQueryChange(value)
		pushDebounced(value)
	}

	const { data: hosts, isFetching } = useQuery(
		hostSearchQueryOptions(debouncedQuery.trim())
	)
	// The field runs ahead of the request, so "no accounts found" waits for the
	// term it actually describes rather than flashing over the previous one.
	const isSettled = debouncedQuery === query && !isFetching

	return (
		<div className='flex flex-col gap-4'>
			<div className='flex flex-col rounded-2xl bg-grey-50 px-4 py-3'>
				<span className='font-medium text-neutral-900 text-sm'>
					{target.name}
				</span>
				<span className='text-neutral-500 text-sm'>
					{t('marquee:assign_host_context_missing_host_id')}
				</span>
			</div>

			<div className='flex flex-col gap-2'>
				<div className='flex flex-col'>
					<span className='font-medium text-neutral-900 text-sm'>
						{t('marquee:assign_host_search_label')}
					</span>
					<span className='text-neutral-500 text-sm'>
						{t('marquee:assign_host_search_hint')}
					</span>
				</div>
				<SearchInput
					autoFocus
					clearLabel={t('marquee:assign_host_search_clear')}
					onChange={handleChange}
					placeholder={t('marquee:assign_host_search_placeholder')}
					value={query}
				/>
			</div>

			{query.length > 0 && (
				<div className='flex flex-col gap-2'>
					{hosts?.map((host) => (
						<HostOption
							host={host}
							isSelected={selectedHostId === host.airbnb_host_id}
							key={host.airbnb_host_id}
							onSelect={() => onSelect(host)}
						/>
					))}
					{/* Nothing found is a real answer here, not an error: the
					    directory holds accounts, so a person's name often
					    returns nothing even when the host exists. */}
					{isSettled && hosts?.length === 0 && (
						<p className='rounded-2xl bg-grey-50 px-4 py-8 text-center text-neutral-500 text-sm'>
							{t('marquee:assign_host_no_results', { query })}
						</p>
					)}
					{!isSettled && (
						<div className='flex justify-center py-8'>
							<Spinner size={20} />
						</div>
					)}
				</div>
			)}
		</div>
	)
}

/** One line of the post-write summary. */
function OutcomeRow({
	label,
	children
}: {
	label: string
	children: React.ReactNode
}) {
	return (
		<div className='flex items-center justify-between gap-4 border-grey-200 border-b px-4 py-3 last:border-b-0'>
			<dt className='shrink-0 text-neutral-500 text-sm'>{label}</dt>
			<dd className='min-w-0 break-all text-right font-medium text-neutral-900 text-sm'>
				{children}
			</dd>
		</div>
	)
}

/**
 * What the write did: the host is on the row either way, and the banner answers
 * the only question that follows, whether the row now reaches Salesforce.
 */
function OutcomeStep({ attendee }: { attendee: MarqueeAttendee }) {
	const { t } = useTranslation()
	const tone = outcomeTone(attendee)
	const isGood = tone === 'ready'

	return (
		<div className='flex flex-col gap-4'>
			<div
				className={`flex items-start gap-3 rounded-2xl px-4 py-3 ${
					isGood ? 'bg-success-100' : 'bg-orange-600/10'
				}`}
			>
				{isGood ? (
					<CheckCircleFillIcon
						className='mt-0.5 shrink-0 text-success-700'
						size={20}
					/>
				) : (
					<AlertTriangleFillIcon
						className='mt-0.5 shrink-0 text-orange-700'
						size={20}
					/>
				)}
				<div className='flex flex-col'>
					<span className='font-medium text-neutral-900 text-sm'>
						{t(outcomeTitleKey[tone], { name: attendee.name })}
					</span>
					<span className='text-neutral-500 text-sm'>
						{t(outcomeBodyKey(attendee))}
					</span>
				</div>
			</div>

			<dl className='rounded-2xl border border-grey-200'>
				<OutcomeRow label={t('marquee:export_col_host_id')}>
					{attendee.airbnb_host_id ?? '-'}
				</OutcomeRow>
				<OutcomeRow label={t('marquee:assign_host_sf_child_account')}>
					{attendee.sf_child_account_id ?? (
						<span className='text-orange-700'>
							{t(
								tone === 'deferred'
									? 'marquee:assign_host_account_pending'
									: 'marquee:assign_host_account_not_found'
							)}
						</span>
					)}
				</OutcomeRow>
				<OutcomeRow label={t('marquee:assign_host_row_status')}>
					<span
						className={`inline-flex items-center rounded-full px-3 py-1.5 font-medium text-xs ${
							isGood
								? 'bg-success-100 text-success-700'
								: 'bg-orange-600/10 text-orange-700'
						}`}
					>
						{t(attendeeStatusToLabelKey[attendee.status])}
					</span>
				</OutcomeRow>
			</dl>
		</div>
	)
}

/**
 * "Assign host": the fix loop for a row the export cannot place, per design
 * (Figma 13.-Marquee 624-8663 / 659-3640 / 662-8612 / 662-9102).
 *
 * Two steps in one surface. First the search, which is the only check a host id
 * ever gets: `assign-host` stores whatever it is handed and verifies no host, so
 * an id the resolver could not return is an id nobody verified. Then the
 * outcome, stated from the write's own response rather than assumed, because
 * assigning a host and resolving its account are two different things and the
 * second can fail on its own.
 *
 * Desktop → `Dialog`, mobile → bottom `Drawer`, like every dialog here.
 */
export function AssignHostDialog({
	eventId,
	eventState,
	target,
	onClose,
	onMarkUnresolvable
}: AssignHostDialogProps) {
	const { t } = useTranslation()
	const isDesktop = useMinWidth('l')
	const queryClient = useQueryClient()

	const [query, setQuery] = useState('')
	const [selected, setSelected] = useState<MarqueeHostSearchResult | null>(
		null
	)
	// The write's answer, which is also what decides the step: while it is null
	// the dialog is the search, once set it is the outcome.
	const [outcome, setOutcome] = useState<MarqueeAttendee | null>(null)

	// Resolving a row clears it from the pending lane, so the roster, the gate
	// and the preview all change: one event prefix covers them.
	const assign = useMutation({
		mutationFn: async ({
			attendeeId,
			hostId
		}: {
			attendeeId: string
			hostId: string
		}) => {
			const response = await assignHost({
				path: { attendeeId },
				body: { airbnb_host_id: hostId }
			})
			return response.data
		},
		onSuccess: async (attendee) => {
			// Settled before the outcome is shown, so closing the dialog on a
			// refreshed grid is not a second wait.
			await queryClient.invalidateQueries({
				queryKey: makeQueryKeys.marquee.events.detail(eventId ?? '')
			})
			setOutcome(attendee)
		},
		onError: (error) => {
			// `assign-host` is one of seven writes that share the 409
			// `invalid-state` code, so the backend's own sentence could mean any
			// of them. This one names the action and the window it needs.
			const refusal = invalidStateMessage({
				error,
				action: 'assign_host',
				eventState
			})
			if (refusal) {
				toast.error(
					t(refusal.key, {
						state: refusal.stateKey ? t(refusal.stateKey) : ''
					})
				)
				return
			}
			errorHandler(error)
		}
	})

	const close = () => {
		onClose()
		// Reset after the close, not on open: the dialog unmounts nothing, and
		// leaving the previous row's state behind would show it for a frame.
		setQuery('')
		setSelected(null)
		setOutcome(null)
	}

	const confirm = () => {
		if (target && selected) {
			assign.mutate({
				attendeeId: target.attendeeId,
				hostId: selected.airbnb_host_id
			})
		}
	}

	// Opens the confirmation ON TOP of this dialog rather than in its place, so
	// cancelling there lands back on the search with its query and selection
	// intact. Only the mark itself ends the errand, and the caller closes this
	// one then.
	const openUnresolvable = () => {
		if (target) {
			onMarkUnresolvable?.(target)
		}
	}

	const open = target !== null
	const title = t('marquee:assign_host_title')

	const body =
		target &&
		(outcome ? (
			<OutcomeStep attendee={outcome} />
		) : (
			<SearchStep
				onQueryChange={setQuery}
				onSelect={setSelected}
				query={query}
				selectedHostId={selected?.airbnb_host_id ?? null}
				target={target}
			/>
		))

	// Giving up on the row stays reachable from both steps, since a host that
	// resolves to no account is exactly when the operator needs it. It is hidden
	// only once the row is settled and exporting.
	const showUnresolvable =
		Boolean(onMarkUnresolvable) &&
		!(outcome && outcomeTone(outcome) === 'ready')

	const footer = outcome ? (
		<>
			{showUnresolvable && (
				<Button
					className='mr-auto'
					onClick={openUnresolvable}
					type='button'
					variant='tertiary'
				>
					{t('marquee:export_mark_unresolvable')}
				</Button>
			)}
			<Button onClick={close} type='button' variant='primary'>
				{t('marquee:assign_host_done')}
			</Button>
		</>
	) : (
		<>
			{showUnresolvable && (
				<Button
					className='mr-auto'
					onClick={openUnresolvable}
					type='button'
					variant='tertiary'
				>
					{t('marquee:export_mark_unresolvable')}
				</Button>
			)}
			<Button onClick={close} type='button' variant='tertiary'>
				{t('marquee:cancel')}
			</Button>
			<Button
				disabled={!selected || assign.isPending}
				onClick={confirm}
				type='button'
				variant='primary'
			>
				{title}
				{assign.isPending && <Spinner size={16} />}
			</Button>
		</>
	)

	if (isDesktop) {
		return (
			<Dialog onOpenChange={(next) => !next && close()} open={open}>
				<DialogContent className='max-w-152 grid-rows-[minmax(0,1fr)_auto]'>
					<DialogBody className='gap-4'>
						<DialogTitle className='text-center'>
							{title}
						</DialogTitle>
						{body}
					</DialogBody>
					<DialogFooter className='flex flex-row items-center justify-end gap-2'>
						{footer}
					</DialogFooter>
				</DialogContent>
			</Dialog>
		)
	}

	return (
		<Drawer onOpenChange={(next) => !next && close()} open={open}>
			<DrawerContent>
				<DrawerHeader className='pb-2'>
					<DrawerTitle className='font-semibold text-neutral-900 text-xl'>
						{title}
					</DrawerTitle>
				</DrawerHeader>
				<div className='min-h-0 flex-1 overflow-y-auto px-4 pb-4'>
					{body}
				</div>
				{/* Three controls on a phone: wrapping keeps the primary whole
				    rather than squeezing all of them onto one line. */}
				<DrawerFooter className='flex-row flex-wrap items-center justify-end gap-2'>
					{footer}
				</DrawerFooter>
			</DrawerContent>
		</Drawer>
	)
}
